import React from "react"
import { Box, Button, Stack, SvgIcon, Typography } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { Provider } from "../../types/Providers"
import { VirtualServicesTag } from "./VirtualServicesTag"
import { VirtualServiceRestrictionNotice } from "./VirtualServiceRestrictionNotice"
import { UsaStates } from "@internal/constants"

// The Carrot Clinic row (providerUuid -1) renders as a standalone hero card
// above the regular results list, not a list item. The orange top stripe and
// brand label distinguish it from indexed clinics; see MEM-762.
export const CARROT_CLINIC_PROVIDER_UUID = -1

export function CarrotClinicCard({
  provider,
  stateCode
}: {
  provider: Provider
  stateCode: string | null
}): JSX.Element {
  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.tokens.borderRadius.md,
        borderBlockStart: `${theme.tokens.borderWidth.lg} solid ${theme.palette.primary.main}`,
        padding: theme.spacing(theme.tokens.spacing.xl)
      })}
    >
      <Stack gap={(theme) => theme.tokens.spacing.md}>
        <BrandLabel />
        <Stack gap={(theme) => theme.tokens.spacing.xxs}>
          <Typography variant="h4" component="h2">
            {provider.providerName}
          </Typography>
          <AvailableInState stateCode={stateCode} />
        </Stack>
        <Typography variant="body1">
          <FormattedMessage defaultMessage="Video visits with Carrot clinicians." />
        </Typography>
        <Box>
          <VirtualServicesTag />
        </Box>
        <Box>
          <Button variant="outlined" color="secondary">
            <FormattedMessage defaultMessage="Book now" />
          </Button>
        </Box>
        {/* Only surface the caveat when the BFF couldn't gate by state
            (non-location searches) — otherwise the state is known-permitted
            and listing the restricted set again is noise. */}
        {!stateCode && (
          <VirtualServiceRestrictionNotice virtualServicesNotAvailableIn={provider.virtualServicesNotAvailableIn} />
        )}
      </Stack>
    </Box>
  )
}

// Layout + typography match the "Exclusive benefits" label pattern in
// ProviderDetailsMainContent so the visual language between hero and regular
// cards stays consistent. The Carrot "A" brand mark is inlined from the
// favicon; the repo has no standalone brand-mark asset today.
function BrandLabel(): JSX.Element {
  return (
    <Box
      sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
    >
      <CarrotBrandMark />
      <Typography variant="chip">
        <FormattedMessage defaultMessage="Carrot's clinical team" />
      </Typography>
    </Box>
  )
}

function CarrotBrandMark(): JSX.Element {
  return (
    <SvgIcon fontSize="small" color="primary" viewBox="0 0 32 32">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.9419 8.81546L9.01584 25.0665H5.33337L14.3344 4.5332H17.6657L26.6667 25.0665H22.8679L15.9419 8.81546Z"
        fill="currentColor"
      />
    </SvgIcon>
  )
}

function AvailableInState({ stateCode }: { stateCode: string | null }): JSX.Element | null {
  const intl = useIntl()
  if (!stateCode) return null
  const state = UsaStates.find((s) => s.id === stateCode)
  if (!state) return null
  const stateName = intl.formatMessage(state.label)
  return (
    <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
      <FormattedMessage defaultMessage="Available across {stateName}" values={{ stateName }} />
    </Typography>
  )
}
